import { useCallback, useEffect, useRef, useState } from 'react';
import { useSessionStorage } from 'react-use';
import { getUserAttributes } from '@noths/polaris-client-user-configuration';

import type { Environment } from 'client-site-chrome-ui/environment';
import { getBasketItemCount } from 'client-site-chrome-ui/services/basket';
import type { Cookies } from 'client-site-chrome-ui/types/cookies';
import { handleAnonymousBasket } from 'client-site-chrome-ui/utils/handleAnonymousBasket';

const basketUpdateEventName = 'basketUpdated';

interface UseBasketParams {
  cookies?: Cookies;
  env?: Environment;
  mergeBasket?: boolean;
}

export const BASKET_STORAGE_KEY = 'CLIENT_SITE_CHROME__BASKET_ITEMS_COUNT';

export const useBasket = ({
  cookies,
  env = 'development',
  mergeBasket = true,
}: UseBasketParams) => {
  const [basketItemsCount, setBasketItemsCount] = useState<number | null>(null);
  const [cachedBasketItemsCount, setCachedBasketItemsCount] = useSessionStorage<number | null>(
    BASKET_STORAGE_KEY,
    basketItemsCount,
  );
  const cancelled = useRef(false);

  useEffect(() => {
    if (cachedBasketItemsCount) {
      setBasketItemsCount(cachedBasketItemsCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateBasketItemsCount = useCallback(async () => {
    const attributes = await getUserAttributes();

    const idToken = attributes?.idToken;
    const count = await getBasketItemCount(env, idToken);
    if (!cancelled.current) {
      setBasketItemsCount(count);
      setCachedBasketItemsCount(count);
    }
  }, [env, setCachedBasketItemsCount]);

  useEffect(() => {
    async function handleMergeAndGetItemsCount() {
      await handleAnonymousBasket(env, mergeBasket, cookies);
      updateBasketItemsCount();
    }

    window.addEventListener('load', handleMergeAndGetItemsCount);

    return () => {
      cancelled.current = true;
      window.removeEventListener('load', handleMergeAndGetItemsCount);
    };
  }, [updateBasketItemsCount, env, cookies]);

  useEffect(() => {
    document.addEventListener(basketUpdateEventName, updateBasketItemsCount);

    return () => {
      document.removeEventListener(basketUpdateEventName, updateBasketItemsCount);
    };
  }, [updateBasketItemsCount]);

  return basketItemsCount;
};
